import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { supabase } from '../lib/supabase';
import api, { UPLOAD_TIMEOUT } from '../lib/api';
import { compressImage } from '../lib/imageCompression';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import RichTextEditor from './RichTextEditor';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { BOHOL_TOWNS, COMMON_BENEFITS } from '../lib/constants';
import { usePricing } from '../hooks/usePricing';
import {
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Loader2,
  RefreshCw,
  Check,
  Wrench,
  MapPin,
  Wallet,
  Clock,
  FileText,
  Image as ImageIcon,
  Upload,
  X,
  Edit,
  Save,
  Ruler,
  HardHat,
  Star
} from 'lucide-react';

/**
 * AI Gig Wizard - Step-by-step questionnaire for AI-powered gig post creation
 * Gigs are one-time tasks/projects, different from ongoing job employment
 */
const AIGigWizard = ({ categories, onComplete, onCancel, onSaveDraft, draftId, autoSaving, initialDraftData }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [generatedPost, setGeneratedPost] = useState(null);
  const [gigImage, setGigImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imagePage, setImagePage] = useState(1);
  const [customImage, setCustomImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [posting, setPosting] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedTitle, setEditedTitle] = useState(''); // Editable AI-generated title
  const [imagePosition, setImagePosition] = useState(50); // Image vertical crop position (0-100)
  const [descriptionLength, setDescriptionLength] = useState('concise'); // 'concise' or 'detailed'
  const [isFeatured, setIsFeatured] = useState(false);
  const [credits, setCredits] = useState(0);
  const { pricing } = usePricing();
  const fileInputRef = React.useRef(null);
  const isSubmittingRef = React.useRef(false);

  // Fetch credits balance for featured option
  useEffect(() => {
    let retries = 0;
    const fetchCredits = async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) {
          if (retries < 3) {
            retries++;
            setTimeout(fetchCredits, 1000);
          }
          return;
        }
        const response = await api.get('/api/credits/balance', {
          headers: { Authorization: `Bearer ${session.session.access_token}` }
        });
        setCredits(response.data.balance || 0);
      } catch (error) {
        console.error('Failed to fetch credits:', error);
      }
    };
    fetchCredits();
  }, []);

  const [formData, setFormData] = useState({
    // Step 1: Task Basics
    task_title: '',
    category: '',
    category_id: '',
    custom_category: '',
    location: '',

    // Step 2: Task Scope (NEW - structured info for better AI output)
    scope_size: 'small', // small, medium, large
    estimated_duration: '', // e.g., "2-3 hours", "1 day", "1 week"
    work_area: '', // e.g., "Kitchen bathroom", "Entire house", "Garden area"
    materials_provided: true, // Does client provide materials?
    tools_provided: false, // Does client provide tools?

    // Step 3: Budget & Timeline
    budget_min: '',
    budget_max: '',
    timeline: 'flexible', // flexible, this_week, urgent

    // Step 4: Task Details
    task_description: '',
    special_requirements: '',
    preferred_experience: 'any', // any, some, experienced
    physical_requirements: '', // e.g., "Must be able to climb ladder"

    // Step 5: Preview (benefits collected here too)
    benefits: []
  });

  // Restore wizard state from draft data when resuming
  useEffect(() => {
    if (!initialDraftData) return;
    const d = initialDraftData;
    setFormData(prev => ({
      ...prev,
      task_title: d.title || '',
      category_id: d.category_id || '',
      category: categories.find(c => c.id === d.category_id)?.name || '',
      location: d.location || '',
      budget_min: d.salary_min || d.budget_min || '',
      budget_max: d.salary_max || d.budget_max || '',
      benefits: d.benefits || [],
    }));
    if (d.description) {
      setGeneratedPost({
        generated_title: d.title || '',
        full_description: d.description,
      });
      setEditedTitle(d.title || '');
      setEditedDescription(d.description);
      if (d.image_url) {
        setCustomImage(d.image_url);
      }
      setImagePosition(d.image_position || 50);
      if (!d._wizardState) {
        setCurrentStep(4); // Jump to preview step (default when no wizard state saved)
      }
    }
    // Restore wizard-specific state from localStorage
    if (d._wizardState) {
      const ws = d._wizardState;
      setFormData(prev => ({
        ...prev,
        scope_size: ws.scope_size || 'small',
        estimated_duration: ws.estimated_duration || '',
        work_area: ws.work_area || '',
        materials_provided: ws.materials_provided !== undefined ? ws.materials_provided : true,
        tools_provided: ws.tools_provided !== undefined ? ws.tools_provided : false,
        timeline: ws.timeline || 'flexible',
        task_description: ws.task_description || '',
        special_requirements: ws.special_requirements || '',
        preferred_experience: ws.preferred_experience || 'any',
        physical_requirements: ws.physical_requirements || '',
      }));
      if (typeof ws.currentStep === 'number') {
        setCurrentStep(ws.currentStep);
      }
    }
  }, [initialDraftData, categories]);

  // Build partial gig data from current wizard state for draft saving
  const buildDraftData = () => {
    const budgetMin = parseInt(formData.budget_min) || 0;
    const budgetMax = parseInt(formData.budget_max) || 0;
    return {
      title: editedTitle || generatedPost?.generated_title || formData.task_title || '',
      description: editedDescription || generatedPost?.full_description || '',
      category_id: formData.category_id || null,
      location: formData.location || '',
      budget_min: budgetMin,
      budget_max: budgetMax,
      benefits: formData.benefits || [],
      image_url: customImage || gigImage?.image_url || null,
      image_position: imagePosition,
      post_type: 'gig',
      _wizardState: {
        currentStep,
        scope_size: formData.scope_size || 'small',
        estimated_duration: formData.estimated_duration || '',
        work_area: formData.work_area || '',
        materials_provided: formData.materials_provided,
        tools_provided: formData.tools_provided,
        timeline: formData.timeline || 'flexible',
        task_description: formData.task_description || '',
        special_requirements: formData.special_requirements || '',
        preferred_experience: formData.preferred_experience || 'any',
        physical_requirements: formData.physical_requirements || '',
      }
    };
  };

  const handleSaveDraft = () => {
    if (!onSaveDraft) return;
    const draftData = buildDraftData();
    if (!draftData.title?.trim()) {
      toast.error('Please enter a task title first');
      return;
    }
    onSaveDraft(draftData);
  };

  // Auto-save every 30s when there's a title
  React.useEffect(() => {
    if (!onSaveDraft || !formData.task_title?.trim()) return;
    const timer = setTimeout(() => {
      const draftData = buildDraftData();
      if (draftData.title?.trim()) onSaveDraft(draftData);
    }, 30000);
    return () => clearTimeout(timer);
  }, [formData, currentStep, editedTitle, editedDescription, generatedPost, customImage, gigImage, imagePosition]); // Auto-save deps

  // Scroll to top of wizard when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Wizard steps - 5 steps now for better AI output
  const steps = [
    { id: 'basics', title: t('gig.aiWizard.stepBasics', 'Task Basics'), icon: Wrench, description: t('gig.aiWizard.whatTask', 'What task do you need done?') },
    { id: 'scope', title: t('gig.aiWizard.stepScope', 'Task Scope'), icon: Ruler, description: t('gig.aiWizard.scopeDesc', 'How big is this task?') },
    { id: 'budget', title: t('gig.aiWizard.stepBudget', 'Budget & Timeline'), icon: Wallet, description: t('gig.aiWizard.budgetTimeline', 'What\'s your budget and timeline?') },
    { id: 'details', title: t('gig.aiWizard.stepDetails', 'Task Details'), icon: FileText, description: t('gig.aiWizard.moreDetails', 'Describe the work needed') },
    { id: 'preview', title: t('gig.aiWizard.stepPreview', 'Preview & Post'), icon: Sparkles, description: t('gig.aiWizard.reviewPost', 'Review your AI-generated gig post') }
  ];

  const handleCategoryChange = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    const isOther = category?.name?.toLowerCase().includes('other');
    setFormData(prev => ({
      ...prev,
      category_id: categoryId,
      category: category?.name || '',
      custom_category: isOther ? prev.custom_category : ''
    }));
  };

  const getValidationError = () => {
    switch (currentStep) {
      case 0: { // Basics
        if (!formData.task_title?.trim()) return 'Please enter a task/gig title';
        if (!formData.category_id) return 'Please select a category';
        const selectedCategory = categories.find(c => c.id === formData.category_id);
        const isOtherCategory = selectedCategory?.name?.toLowerCase().includes('other');
        if (isOtherCategory && !formData.custom_category.trim()) return 'Please enter a custom category name';
        if (!formData.location) return 'Please select a location';
        return null;
      }
      case 1: // Task Scope
        if (!formData.scope_size) return 'Please select a task scope size';
        return null;
      case 2: { // Budget & Timeline
        if (formData.budget_min || formData.budget_max) {
          const minVal = parseFloat(formData.budget_min);
          const maxVal = parseFloat(formData.budget_max);
          if (formData.budget_min && (isNaN(minVal) || minVal <= 0)) return 'Budget minimum must be a positive number';
          if (formData.budget_max && (isNaN(maxVal) || maxVal <= 0)) return 'Budget maximum must be a positive number';
          if (formData.budget_min && formData.budget_max && minVal > maxVal) return 'Budget minimum cannot be greater than maximum';
        }
        if (!formData.timeline) return 'Please select a timeline';
        return null;
      }
      case 3: // Details
        if (!formData.task_description?.trim() || formData.task_description.trim().length < 20) return 'Please enter a task description (at least 20 characters)';
        return null;
      case 4: return generatedPost ? null : 'Please generate a gig post first';
      default: return 'Unknown step';
    }
  };

  const canProceed = () => getValidationError() === null;

  const handleNext = async () => {
    const error = getValidationError();
    if (error) {
      toast.error(error);
      return;
    }
    if (currentStep === 3) {
      // Step 3 (Details) -> Step 4 (Preview): Generate the gig post before moving to preview
      // generateGigPost will set currentStep to 4 on success
      await generateGigPost();
    } else if (currentStep < steps.length - 1) {
      // For other steps, just increment
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const generateGigPost = async () => {
    setGenerating(true);
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast.error(t('common.pleaseLogin', 'Please log in'));
        return;
      }

      const selectedCategory = categories.find(c => c.id === formData.category_id);
      const isOtherCategory = selectedCategory?.name?.toLowerCase().includes('other');
      const categoryToSend = isOtherCategory && formData.custom_category.trim()
        ? formData.custom_category.trim()
        : formData.category;

      // Send ALL collected data to AI for better output
      const response = await api.post(
        '/api/ai/generate-gig-post',
        {
          task_title: formData.task_title,
          category: categoryToSend,
          location: formData.location,
          // Scope information (NEW)
          scope_size: formData.scope_size,
          estimated_duration: formData.estimated_duration || null,
          work_area: formData.work_area || null,
          materials_provided: formData.materials_provided,
          tools_provided: formData.tools_provided,
          // Budget & Timeline
          budget_min: formData.budget_min ? Math.floor(Number(formData.budget_min)) : null,
          budget_max: formData.budget_max ? Math.floor(Number(formData.budget_max)) : null,
          timeline: formData.timeline,
          // Task details
          task_description: formData.task_description,
          special_requirements: formData.special_requirements || null,
          preferred_experience: formData.preferred_experience,
          physical_requirements: formData.physical_requirements || null,
          benefits: formData.benefits,
          description_length: descriptionLength
        },
        {
          headers: {
            Authorization: `Bearer ${session.data.session.access_token}`
          }
        }
      );

      if (response.data.success) {
        setGeneratedPost(response.data.data);
        setEditedTitle(response.data.data.generated_title || ''); // Initialize editable title
        setEditedDescription('');
        // Load image for gig
        await loadGigImage(categoryToSend);
        // Move to preview step (step 4) on success
        setCurrentStep(4);
      } else {
        toast.error(response.data.detail || t('gig.aiWizard.generationFailed', 'Failed to generate gig post'));
      }
    } catch (error) {
      console.error('Error generating gig post:', error);
      toast.error(error.response?.data?.detail || t('gig.aiWizard.generationFailed', 'Failed to generate gig post'));
    } finally {
      setGenerating(false);
    }
  };

  const loadGigImage = async (category) => {
    setImageLoading(true);
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) return;

      const response = await api.get('/api/ai/job-image', {
        params: {
          category: category,
          page: imagePage,
          job_title: formData.task_title
        },
        headers: { Authorization: `Bearer ${session.data.session.access_token}` }
      });

      if (response.data.success) {
        setGigImage(response.data.data);
      }
    } catch (error) {
      console.error('Error loading gig image:', error);
    } finally {
      setImageLoading(false);
    }
  };

  // Refresh/regenerate the gig image
  const refreshImage = () => {
    setImagePage(prev => prev + 1);
    loadGigImage(formData.category);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file (iOS may report empty string for HEIC camera photos)
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    const hasValidExt = /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name || '');
    if (!validTypes.includes(file.type.toLowerCase()) && !(file.type === '' && hasValidExt)) {
      toast.error(t('upload.invalidType', 'Please upload a JPG, PNG, WebP, or HEIC image'));
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t('upload.tooLarge', 'Image must be less than 20MB'));
      return;
    }

    setUploadingImage(true);
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast.error(t('common.pleaseLogin', 'Please log in'));
        return;
      }

      // Compress before uploading
      const compressed = await compressImage(file, 'jobImage');

      const formDataUpload = new FormData();
      formDataUpload.append('file', compressed);

      const response = await api.post('/api/upload-image', formDataUpload, {
        headers: {
          Authorization: `Bearer ${session.data.session.access_token}`
        },
        timeout: UPLOAD_TIMEOUT,
      });

      if (response.data.success) {
        setCustomImage(response.data.image_url);
        toast.success(t('upload.success', 'Image uploaded successfully'));
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(t('upload.failed', 'Failed to upload image'));
    } finally {
      setUploadingImage(false);
      // Reset file input so user can re-select same file if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleComplete = async () => {
    if (isSubmittingRef.current) return;

    // Validate before posting
    if (!generatedPost || !generatedPost.full_description) {
      toast.error('Please generate a gig post first');
      return;
    }

    isSubmittingRef.current = true;
    setPosting(true);
    let gigPosted = false;
    let gigData = null;

    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        toast.error(t('common.pleaseLogin', 'Please log in to post'));
        return;
      }

      // Budget values should be integers for backend
      const budgetMin = formData.budget_min ? Math.floor(Number(formData.budget_min)) : null;
      const budgetMax = formData.budget_max ? Math.floor(Number(formData.budget_max)) : null;

      // Use edited description if available, otherwise use generated
      let finalDescription = editedDescription || generatedPost.full_description;

      // Defense-in-depth XSS prevention (backend sanitizes with bleach)
      if (finalDescription) {
        finalDescription = finalDescription
          // Remove script tags
          .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
          // Remove event handlers (onclick, onerror, onload, etc.)
          .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
          .replace(/\s*on\w+\s*=[^\s>]*/gi, '')
          // Remove javascript: URLs
          .replace(/javascript\s*:/gi, '')
          // Remove data: URLs that could contain scripts
          .replace(/data\s*:\s*text\/html/gi, '');
      }

      // Prepare the final gig data
      // Note: urgent_hiring, no_experience_needed, with_housing columns don't exist in gigs table
      const postData = {
        title: editedTitle || generatedPost.generated_title || formData.task_title,
        description: finalDescription,
        category_id: formData.category_id,
        location: formData.location,
        post_type: 'gig', // Explicitly mark as gig
        budget_min: budgetMin,
        budget_max: budgetMax,
        benefits: formData.benefits,
        image_url: customImage || gigImage?.image_url || null,
        image_position: imagePosition,
        is_featured: isFeatured
      };

      // Post to gigs endpoint (or publish draft via PUT)
      const headers = { Authorization: `Bearer ${session.data.session.access_token}` };
      let response;
      if (draftId) {
        // Strip fields blocked by backend PUT endpoint
        const { is_featured, post_type, ...publishData } = postData;
        response = await api.put(`/api/gigs/${draftId}`, { ...publishData, status: 'open' }, { headers });
      } else {
        response = await api.post('/api/gigs', postData, { headers });
      }

      // Check for success - require explicit success:true from backend
      if (response.data && response.data.success === true) {
        gigPosted = true;
        gigData = response.data.gig || postData;
      } else if (response.data && response.data.detail) {
        // Explicit error from backend
        throw new Error(response.data.detail);
      } else {
        throw new Error(response.data?.detail || 'Failed to post gig');
      }

    } catch (error) {
      console.error('Error posting gig:', error);
      const errorDetail = error.response?.data?.detail || error.message || t('gig.postFailed', 'Failed to post gig');
      toast.error(errorDetail);
    } finally {
      isSubmittingRef.current = false;
      setPosting(false);

      // Call onComplete on success - do this in finally to ensure it happens
      if (gigPosted) {
        toast.success(t('gig.postSuccess', 'Gig posted successfully!'));
        onComplete(gigData);
      }
    }
  };

  // Render Step 1: Task Basics
  const renderBasicsStep = () => (
    <div className="space-y-6">
      {/* Task Title */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">{t('gig.aiWizard.taskTitle', 'What task do you need done?')} *</Label>
        <Input
          placeholder={t('gig.aiWizard.taskTitlePlaceholder', 'e.g., Paint my fence, Fix leaky faucet, Clean my house')}
          value={formData.task_title}
          onChange={(e) => setFormData({ ...formData, task_title: e.target.value })}
          className="rounded-xl text-lg py-6"
        />
      </div>

      {/* Category */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">{t('gig.aiWizard.category', 'What category?')} *</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleCategoryChange(cat.id)}
              className={`p-4 rounded-xl border-2 text-left transition-colors ${
                formData.category_id === cat.id
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                  : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
            >
              <div className="font-medium">{cat.name}</div>
            </button>
          ))}
        </div>
        {/* Custom category input when "Other" is selected */}
        {formData.category_id && categories.find(c => c.id === formData.category_id)?.name?.toLowerCase().includes('other') && (
          <div className="mt-3 p-4 border-2 border-primary/30 rounded-xl bg-primary/5">
            <Label htmlFor="custom_category" className="text-sm font-medium mb-2 block">
              {t('gig.aiWizard.specifyCategory', 'Please specify your category')} *
            </Label>
            <Input
              id="custom_category"
              placeholder={t('gig.aiWizard.customCategoryPlaceholder', 'e.g., Pet Grooming, Event Setup, Moving Help')}
              value={formData.custom_category}
              onChange={(e) => setFormData({ ...formData, custom_category: e.target.value })}
              className="rounded-xl"
            />
          </div>
        )}
      </div>

      {/* Location */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">{t('gig.aiWizard.location', 'Where is this task?')} *</Label>
        <Select
          value={formData.location}
          onValueChange={(value) => setFormData({ ...formData, location: value })}
        >
          <SelectTrigger className="rounded-xl">
            <SelectValue placeholder={t('gig.aiWizard.selectLocation', 'Select location')} />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {BOHOL_TOWNS.map((town) => (
              <SelectItem key={town} value={town}>{town}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  // Render Step 2: Task Scope (NEW - helps AI understand the task better)
  const renderScopeStep = () => (
    <div className="space-y-6">
      {/* Scope Size */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('gig.aiWizard.scopeSize', 'How big is this task?')} *</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { value: 'small', label: t('scope.small', 'Small'), icon: '📦', desc: t('scope.smallDesc', 'Quick task, few hours or less') },
            { value: 'medium', label: t('scope.medium', 'Medium'), icon: '📋', desc: t('scope.mediumDesc', 'Half day to full day work') },
            { value: 'large', label: t('scope.large', 'Large'), icon: '🏗️', desc: t('scope.largeDesc', 'Multiple days or complex project') }
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFormData({ ...formData, scope_size: option.value })}
              className={`p-4 rounded-xl border-2 text-center transition-colors ${
                formData.scope_size === option.value
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                  : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
            >
              <div className="text-2xl mb-1">{option.icon}</div>
              <div className="font-semibold">{option.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{option.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Estimated Duration */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">{t('gig.aiWizard.duration', 'Estimated Duration')} <span className="text-muted-foreground font-normal">(Optional)</span></Label>
        <Input
          placeholder={t('gig.aiWizard.durationPlaceholder', 'e.g., 2-3 hours, 1 day, about a week')}
          value={formData.estimated_duration}
          onChange={(e) => setFormData({ ...formData, estimated_duration: e.target.value })}
          className="rounded-xl"
        />
      </div>

      {/* Work Area */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">{t('gig.aiWizard.workArea', 'Where exactly is the work?')} <span className="text-muted-foreground font-normal">(Optional)</span></Label>
        <Input
          placeholder={t('gig.aiWizard.workAreaPlaceholder', 'e.g., Kitchen and bathroom, Entire house, Back garden, Office building')}
          value={formData.work_area}
          onChange={(e) => setFormData({ ...formData, work_area: e.target.value })}
          className="rounded-xl"
        />
      </div>

      {/* Materials & Tools Provided */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('gig.aiWizard.whatProvided', 'What will you provide?')}</Label>
        <div className="grid grid-cols-2 gap-4">
          <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
            formData.materials_provided ? 'border-primary bg-primary/10' : 'border-gray-200 hover:border-primary/50'
          }`}>
            <input
              type="checkbox"
              checked={formData.materials_provided}
              onChange={(e) => setFormData({ ...formData, materials_provided: e.target.checked })}
              className="w-5 h-5 rounded"
            />
            <div>
              <div className="font-medium">🧱 {t('gig.aiWizard.materials', 'Materials')}</div>
              <div className="text-xs text-muted-foreground">{t('gig.aiWizard.materialsDesc', 'Paint, parts, supplies, etc.')}</div>
            </div>
          </label>
          <label className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
            formData.tools_provided ? 'border-primary bg-primary/10' : 'border-gray-200 hover:border-primary/50'
          }`}>
            <input
              type="checkbox"
              checked={formData.tools_provided}
              onChange={(e) => setFormData({ ...formData, tools_provided: e.target.checked })}
              className="w-5 h-5 rounded"
            />
            <div>
              <div className="font-medium">🔧 {t('gig.aiWizard.tools', 'Tools')}</div>
              <div className="text-xs text-muted-foreground">{t('gig.aiWizard.toolsDesc', 'Worker can use your tools')}</div>
            </div>
          </label>
        </div>
      </div>
    </div>
  );

  // Render Step 3: Budget & Timeline
  const renderBudgetStep = () => (
    <div className="space-y-6">
      {/* Budget Range */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('gig.aiWizard.budget', 'Budget Range')} <span className="text-muted-foreground font-normal">(Optional)</span></Label>
        <p className="text-sm text-muted-foreground">
          {t('gig.aiWizard.budgetHint', 'Leave blank if you want workers to propose their rates')}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="budget_min">{t('gig.minBudget', 'Minimum (₱)')}</Label>
            <Input
              id="budget_min"
              type="number"
              placeholder="500"
              value={formData.budget_min}
              onChange={(e) => setFormData({ ...formData, budget_min: e.target.value })}
              onWheel={(e) => e.target.blur()}
              className="rounded-xl"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="budget_max">{t('gig.maxBudget', 'Maximum (₱)')}</Label>
            <Input
              id="budget_max"
              type="number"
              placeholder="1000"
              value={formData.budget_max}
              onChange={(e) => setFormData({ ...formData, budget_max: e.target.value })}
              onWheel={(e) => e.target.blur()}
              className="rounded-xl"
            />
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('gig.aiWizard.timeline', 'When do you need this done?')} *</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { value: 'urgent', label: t('timeline.urgent', 'ASAP'), icon: '🔴', desc: t('timeline.urgentDesc', 'Need it done immediately') },
            { value: 'this_week', label: t('timeline.thisWeek', 'This Week'), icon: '📅', desc: t('timeline.thisWeekDesc', 'Within the next 7 days') },
            { value: 'flexible', label: t('timeline.flexible', 'Flexible'), icon: '🕐', desc: t('timeline.flexibleDesc', 'No rush, anytime is fine') }
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFormData({ ...formData, timeline: option.value })}
              className={`p-4 rounded-xl border-2 text-center transition-colors ${
                formData.timeline === option.value
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                  : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
            >
              <div className="text-2xl mb-1">{option.icon}</div>
              <div className="font-semibold">{option.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{option.desc}</div>
            </button>
          ))}
        </div>
      </div>

    </div>
  );

  // Render Step 4: Task Details
  const renderDetailsStep = () => (
    <div className="space-y-6">
      {/* Task Description */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">{t('gig.aiWizard.taskDescription', 'Describe the task in detail')} *</Label>
        <p className="text-sm text-muted-foreground">
          {t('gig.aiWizard.taskDescriptionHint', 'The more details you provide, the better the AI can create your gig post. Include measurements, quantities, specific issues, etc.')}
        </p>
        <Textarea
          placeholder={t('gig.aiWizard.taskDescriptionPlaceholder', 'e.g., I need someone to paint the exterior fence of my house. The fence is about 20 meters long and 1.5 meters high. It\'s a wooden fence that needs sanding first. There are some areas with peeling paint that need extra attention. I want it painted white to match the house.')}
          value={formData.task_description}
          onChange={(e) => setFormData({ ...formData, task_description: e.target.value.slice(0, 2000) })}
          rows={6}
          maxLength={2000}
          className="rounded-xl"
        />
        <p className={`text-xs ${formData.task_description.length < 20 ? 'text-red-500' : formData.task_description.length > 1800 ? 'text-amber-600' : 'text-muted-foreground'}`}>
          {formData.task_description.length}/2000 {t('common.characters', 'characters')} ({t('common.minRequired', 'min 20 required')})
        </p>
      </div>

      {/* Description Length Toggle */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">📏 {t('gig.aiWizard.descriptionLength', 'Description Length')}</Label>
        <p className="text-sm text-muted-foreground">
          {t('gig.aiWizard.descriptionLengthHint', 'Choose how long the AI-generated description should be')}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setDescriptionLength('detailed')}
            className={`flex-1 p-3 rounded-xl border-2 text-center transition-colors ${
              descriptionLength === 'detailed'
                ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
            }`}
          >
            <div className="font-semibold">{t('gig.aiWizard.detailed', 'Detailed')}</div>
            <div className="text-xs text-muted-foreground">{t('gig.aiWizard.detailedDesc', '4-5 paragraphs')}</div>
          </button>
          <button
            type="button"
            onClick={() => setDescriptionLength('concise')}
            className={`flex-1 p-3 rounded-xl border-2 text-center transition-colors ${
              descriptionLength === 'concise'
                ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
            }`}
          >
            <div className="font-semibold">{t('gig.aiWizard.concise', 'Concise')}</div>
            <div className="text-xs text-muted-foreground">{t('gig.aiWizard.conciseDesc', '2-3 paragraphs')}</div>
          </button>
        </div>
      </div>

      {/* Preferred Experience Level */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('gig.aiWizard.preferredExperience', 'Preferred Experience Level')}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { value: 'any', label: t('exp.any', 'Anyone'), icon: '👋', desc: t('exp.anyDesc', 'Beginners welcome') },
            { value: 'some', label: t('exp.some', 'Some Experience'), icon: '👍', desc: t('exp.someDesc', 'Has done this before') },
            { value: 'experienced', label: t('exp.experienced', 'Experienced'), icon: '⭐', desc: t('exp.experiencedDesc', 'Skilled professional') }
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setFormData({ ...formData, preferred_experience: option.value })}
              className={`p-3 rounded-xl border-2 text-center transition-colors ${
                formData.preferred_experience === option.value
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                  : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
            >
              <div className="text-xl mb-1">{option.icon}</div>
              <div className="font-medium text-sm">{option.label}</div>
              <div className="text-xs text-muted-foreground">{option.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Special Requirements */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">{t('gig.aiWizard.specialRequirements', 'Special Requirements')} <span className="text-muted-foreground font-normal">(Optional)</span></Label>
        <Textarea
          placeholder={t('gig.aiWizard.specialRequirementsPlaceholder', 'e.g., Must have own transportation, Need to work on weekends, Should be comfortable working at heights')}
          value={formData.special_requirements}
          onChange={(e) => setFormData({ ...formData, special_requirements: e.target.value.slice(0, 500) })}
          rows={2}
          maxLength={500}
          className="rounded-xl"
        />
      </div>

      {/* Physical Requirements */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">{t('gig.aiWizard.physicalRequirements', 'Physical Requirements')} <span className="text-muted-foreground font-normal">(Optional)</span></Label>
        <Input
          placeholder={t('gig.aiWizard.physicalPlaceholder', 'e.g., Must be able to lift heavy items, Climb ladders, Work outdoors in sun')}
          value={formData.physical_requirements}
          onChange={(e) => setFormData({ ...formData, physical_requirements: e.target.value })}
          className="rounded-xl"
        />
      </div>

      {/* Benefits Selection */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t('gig.aiWizard.additionalBenefits', 'Additional Benefits You Provide')}</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border rounded-xl bg-muted/30">
          {COMMON_BENEFITS.map((benefit) => (
            <label key={benefit} className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.benefits.includes(benefit)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({ ...formData, benefits: [...formData.benefits, benefit] });
                  } else {
                    setFormData({ ...formData, benefits: formData.benefits.filter(b => b !== benefit) });
                  }
                }}
                className="rounded"
              />
              <span className="text-sm">{benefit}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  // Render Step 5: Preview
  const renderPreviewStep = () => (
    <div className="space-y-6">
      {generatedPost ? (
        <>
          {/* Generated Title (Editable) */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">📝 {t('gig.aiWizard.generatedTitle', 'Generated Title')}</Label>
            <textarea
              value={editedTitle}
              onChange={(e) => {
                setEditedTitle(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              ref={(el) => {
                if (el) {
                  el.style.height = 'auto';
                  el.style.height = el.scrollHeight + 'px';
                }
              }}
              rows={1}
              className="w-full text-xl font-bold rounded-xl py-3 px-3 border border-input bg-background resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              placeholder={generatedPost.generated_title || formData.task_title}
            />
          </div>

          {/* Image Selection */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">📷 {t('gig.aiWizard.gigImage', 'Gig Image')}</Label>
            <div className="grid grid-cols-2 gap-4">
              {/* AI Suggested Image */}
              <div
                className={`relative rounded-xl overflow-hidden border-2 cursor-pointer transition-colors ${
                  !customImage ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200 hover:border-primary/50'
                }`}
                onClick={() => setCustomImage(null)}
              >
                {imageLoading ? (
                  <div className="aspect-video flex items-center justify-center bg-gray-100">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : gigImage?.image_url ? (
                  <img src={gigImage.image_url} alt="Suggested" className="aspect-video object-cover" style={{ objectPosition: `center ${imagePosition}%` }} />
                ) : (
                  <div className="aspect-video flex items-center justify-center bg-gray-100">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                  AI Suggested
                </div>
              </div>

              {/* Custom Upload */}
              <div
                className={`relative rounded-xl overflow-hidden border-2 cursor-pointer transition-colors ${
                  customImage ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200 hover:border-primary/50 border-dashed'
                }`}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadingImage ? (
                  <div className="aspect-video flex items-center justify-center bg-gray-100">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : customImage ? (
                  <img src={customImage} alt="Custom" className="aspect-video object-cover" style={{ objectPosition: `center ${imagePosition}%` }} />
                ) : (
                  <div className="aspect-video flex flex-col items-center justify-center bg-gray-50">
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-500">Upload your own</span>
                  </div>
                )}
                {customImage && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setCustomImage(null); }}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
            {/* Get Different Image button */}
            {!customImage && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={refreshImage}
                disabled={imageLoading}
                className="mt-2 rounded-xl"
              >
                {imageLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {t('gig.aiWizard.getDifferentImage', 'Get Different Image')}
              </Button>
            )}
          </div>

          {/* Image Position Slider */}
          {(gigImage?.image_url || customImage) && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">🔲 {t('gig.aiWizard.imagePosition', 'Adjust Image Position')}</Label>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">Top</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={imagePosition}
                  onChange={(e) => setImagePosition(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-gray-200 rounded-lg cursor-pointer accent-primary"
                />
                <span className="text-xs text-muted-foreground">Bottom</span>
              </div>
            </div>
          )}

          {/* Generated Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">📄 {t('gig.aiWizard.description', 'Description')}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (editingDescription) {
                    setEditingDescription(false);
                  } else {
                    setEditedDescription(editedDescription || generatedPost.full_description);
                    setEditingDescription(true);
                  }
                }}
              >
                {editingDescription ? (
                  <>
                    <Save className="w-4 h-4 mr-1" />
                    {t('common.done', 'Done')}
                  </>
                ) : (
                  <>
                    <Edit className="w-4 h-4 mr-1" />
                    {t('common.edit', 'Edit')}
                  </>
                )}
              </Button>
            </div>
            {editingDescription ? (
              <RichTextEditor
                value={editedDescription}
                onChange={(value) => setEditedDescription(value)}
                placeholder={t('gig.aiWizard.editDescription', 'Edit the description...')}
                className="rounded-xl"
              />
            ) : (
              <div
                className="p-4 bg-gray-50 rounded-xl border prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editedDescription || generatedPost.full_description || '') }}
              />
            )}
          </div>

          {/* Budget Display */}
          {(formData.budget_min || formData.budget_max) && (
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <div className="font-semibold text-green-800">
                💰 {t('gig.budget', 'Budget')}:
                {formData.budget_min && formData.budget_max
                  ? ` ₱${parseFloat(formData.budget_min).toLocaleString()} - ₱${parseFloat(formData.budget_max).toLocaleString()}`
                  : formData.budget_min
                    ? ` Starting at ₱${parseFloat(formData.budget_min).toLocaleString()}`
                    : ` Up to ₱${parseFloat(formData.budget_max).toLocaleString()}`
                }
              </div>
            </div>
          )}

          {/* Regenerate Button */}
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              onClick={generateGigPost}
              disabled={generating}
              className="rounded-xl"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('gig.aiWizard.regenerating', 'Regenerating...')}
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('gig.aiWizard.regenerate', 'Regenerate Post')}
                </>
              )}
            </Button>
          </div>

          {/* Feature Option */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-secondary/50 transition-colors">
              <Checkbox
                id="ai-gig-wizard-featured"
                checked={isFeatured}
                onCheckedChange={(checked) => setIsFeatured(checked === true)}
                disabled={credits < (pricing.featured_post || 30)}
              />
              <div className="flex-1">
                <label htmlFor="ai-gig-wizard-featured" className="text-sm font-medium cursor-pointer flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500" />
                  Feature this posting ({pricing.featured_post || 30} credits)
                </label>
                <p className="text-xs text-muted-foreground mt-1">
                  Featured postings appear at the top for 7 days. Your balance: {credits} credits
                </p>
                {credits < (pricing.featured_post || 30) && (
                  <p className="text-xs text-red-500 mt-1">Insufficient credits.</p>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
          <p className="text-lg font-medium">{t('gig.aiWizard.generating', 'Generating your gig post...')}</p>
          <p className="text-muted-foreground">{t('gig.aiWizard.aiCrafting', 'Our AI is crafting the perfect gig listing')}</p>
        </div>
      )}
    </div>
  );

  return (
    <Card className="border-2">
      <CardHeader className="border-b bg-gradient-to-r from-orange-500/5 to-orange-500/10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-500/10 rounded-lg">
            <Sparkles className="w-6 h-6 text-orange-500" />
          </div>
          <div>
            <CardTitle className="text-xl">{t('gig.aiWizard.title', 'AI Gig Wizard')}</CardTitle>
            <CardDescription>{t('gig.aiWizard.subtitle', 'Create a professional gig post in minutes')}</CardDescription>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mt-6">
          {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 shrink-0 rounded-full border-2 transition-colors ${
                  isActive ? 'border-orange-500 bg-orange-500 text-white' :
                  isCompleted ? 'border-orange-500 bg-orange-500/10 text-orange-500' :
                  'border-gray-200 text-gray-400'
                }`}>
                  {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-4 sm:w-12 md:w-24 h-1 mx-1 sm:mx-2 rounded ${
                    isCompleted ? 'bg-orange-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            );
          })}
        </div>
      </CardHeader>

      <CardContent className="p-6">
        {/* Step Title */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold">{steps[currentStep].title}</h3>
          <p className="text-muted-foreground">{steps[currentStep].description}</p>
        </div>

        {/* Step Content */}
        {currentStep === 0 && renderBasicsStep()}
        {currentStep === 1 && renderScopeStep()}
        {currentStep === 2 && renderBudgetStep()}
        {currentStep === 3 && renderDetailsStep()}
        {currentStep === 4 && renderPreviewStep()}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6 border-t">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={currentStep === 0 ? onCancel : handleBack}
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {currentStep === 0 ? t('common.cancel', 'Cancel') : t('common.back', 'Back')}
            </Button>
            {onSaveDraft && formData.task_title?.trim() && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={autoSaving}
                className="rounded-xl"
              >
                {autoSaving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('postJob.saving', 'Saving...')}</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" />{t('postJob.saveDraft', 'Save Draft')}</>
                )}
              </Button>
            )}
          </div>

          {currentStep < steps.length - 1 ? (
            <Button
              type="button"
              onClick={handleNext}
              disabled={generating}
              className="rounded-xl bg-orange-500 hover:bg-orange-600"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('gig.aiWizard.generating', 'Generating...')}
                </>
              ) : (
                <>
                  {t('common.next', 'Next')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleComplete}
              disabled={posting}
              className="rounded-xl bg-orange-500 hover:bg-orange-600"
            >
              {posting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {t('gig.aiWizard.posting', 'Posting...')}
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {t('gig.aiWizard.postGig', 'Post Gig')}
                </>
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AIGigWizard;
